// 檢視紀錄「批改中/失敗」列的純邏輯（kanban 106957 R2）。
//
// 批次批改「完成後」batch 後端才把紀錄寫回高中 classrecord;
// open/running(以及 failed/prohibited)期間只存在 batch 後端的 batch-results,
// 檢視紀錄頁得自己拉來合併顯示。batch-results 不含 bookId/chapterId/classCode,
// 章節全靠送單時寫入的 compositionTitle === 章節名 字串對映;班級容器則以
// 高中班級碼對應(resolveBatchClass,見 services/batchApi.ts ensureBatchClass)。
//
// 這裡只做資料歸類/對映/排序,不碰 DOM 也不打 API — 方便 vitest(node)直測。

/** batch-results 單筆(僅列這裡用得到的欄位;raw status: open/running/finish/failed/prohibited) */
export interface BatchResultLike {
  batchResultID?: string;
  compositionID?: string;
  compositionTitle?: string;
  seatNo?: string;
  userName?: string;
  status?: string;
  createdAt?: string;
}

export type BatchGradingUiStatus = "grading" | "done" | "failed" | "prohibited";

/** 檢視紀錄表格裡一條「批改中/失敗」列 */
export interface PendingBatchRow {
  key: string; // render key:batchResultID 優先,缺 ID 時以欄位+序號組出唯一值
  resultID: string; // batchResultID 原值;空字串=無,「重新批改」鈕據此隱藏(打不了 API)
  seatNo: string;
  seatNoInt: number;
  userName: string;
  status: Exclude<BatchGradingUiStatus, "done">;
  createdAt: string; // 已經 normalizeBatchUtcTime 補 Z 的 UTC 字串;可為空字串(後端缺值時)
}

export interface GroupPendingResult {
  byChapter: Partial<Record<string, PendingBatchRow[]>>;
  /** 對不上任何章節的 compositionTitle(去重)— 呼叫端 console.warn 用(B4) */
  unmatchedTitles: string[];
}

/**
 * batch raw status → UI 狀態。
 * 沿用國中 batchApiUtils summarizeStatuses 的歸類:finish→done、
 * failed/prohibited 各自保留(顯示文案不同,D3)、其餘一律 grading —
 * 後端未來加新中間態時寧可顯示批改中,也不能誤判成失敗或完成。
 */
export const classifyBatchStatus = (status: unknown): BatchGradingUiStatus => {
  if (status === "finish") return "done";
  if (status === "failed") return "failed";
  if (status === "prohibited") return "prohibited";
  return "grading";
};

/**
 * raw 清單層級的批改中判斷(目前僅供測試釘住歸類語意;
 * 元件的輪詢條件請用 hasGradingRows — 吃分組後結果,unmatched 不驅動輪詢)。
 * failed/prohibited 是終端狀態,絕不可算進來 — 算進來就是 10 秒一次的無限輪詢(B7)。
 */
export const hasGradingItems = (items: unknown): boolean =>
  Array.isArray(items) &&
  items.some(
    (item: BatchResultLike) => classifyBatchStatus(item?.status) === "grading"
  );

/**
 * 輪詢條件:吃 groupPendingRowsByChapter 分組後的結果,
 * 僅「有對上章節的批改中列」才輪詢 — 全 unmatched(別書題目/章節改名)
 * 顯示不出來,不該驅動 10 秒一次的背景請求。B7 同樣適用:failed/prohibited 不算。
 */
export const hasGradingRows = (
  byChapter: Partial<Record<string, PendingBatchRow[]>>
): boolean =>
  Object.values(byChapter).some((rows) =>
    (rows ?? []).some((row) => row.status === "grading")
  );

/** 批次系統班級容器(僅列解析用得到的欄位;classCode 舊容器為 null/缺欄位) */
export interface BatchClassLike {
  classID?: string;
  className?: string;
  classCode?: string | null;
}

export type BatchClassResolution =
  | { kind: "match"; classID: string } // 班級碼相符(或無碼可比時的同名無碼容器),直接用
  | { kind: "adopt"; classID: string } // 同名且尚未寫入班級碼的舊容器,送單時 PUT 認領
  | { kind: "create" }; // 沒有可用容器,送單時 POST 新建

/**
 * 高中班級 → batch 班級容器的解析(kanban 106957),送單 ensureBatchClass 與
 * 檢視紀錄 findBatchClassId 共用同一套,兩邊才會選到同一個容器。
 *
 * 高中正式紀錄跟著班級碼(邀請碼)走;老師刪班重建同名班會拿到新碼,
 * 若仍以班名對容器,舊班在途批改會混進新班、新班也拿不到紀錄。所以:
 *   (a) classCode 相符 → match(班名改過也認得)
 *   (b) 沒有 → 同名且「尚未寫入班級碼」的舊容器 → adopt(同名多個取第一個,沿用舊語意);
 *       classCode 為空時無碼可認領,同名無碼容器直接視為 match
 *   (c) 都沒有 → create。同名但已被別的碼認領的容器,一律不接手
 * 非陣列(API 掛掉)視同空清單 → create,不炸(B3)。
 */
export const resolveBatchClass = (
  list: unknown,
  className: string,
  classCode: string
): BatchClassResolution => {
  if (!Array.isArray(list)) return { kind: "create" };
  const classes = list as BatchClassLike[];

  if (classCode) {
    const byCode = classes.find((c) => c?.classCode === classCode);
    if (byCode?.classID) return { kind: "match", classID: byCode.classID };
  }

  if (className) {
    const legacy = classes.find(
      (c) => c?.className === className && !c?.classCode
    );
    if (legacy?.classID) {
      return classCode
        ? { kind: "adopt", classID: legacy.classID }
        : { kind: "match", classID: legacy.classID };
    }
  }

  return { kind: "create" };
};

/**
 * 檢視紀錄用:以班級碼對出 batch 班級 ID,找不到時退回同名且無碼的舊容器
 * (老師還沒重新送單認領前,既有班級的批改中列不會消失)。
 * 同名但已被別的碼認領 → null,視同無批改中資料(B8)— 刪班重建的新班不得看到舊班進度。
 */
export const findBatchClassId = (
  list: unknown,
  target: { className: string; classCode: string }
): string | null => {
  const resolved = resolveBatchClass(list, target.className, target.classCode);
  return resolved.kind === "create" ? null : resolved.classID;
};

// Date.parse 失敗回 NaN,直接進 sort comparator 會讓排序結果不定
const timeOf = (value: string): number => Date.parse(value) || 0;

// 「YYYY-MM-DD(T或空白)HH:mm[:ss[.SSS]]」且結尾沒有時區標記(Z 或 ±hh:mm)
const TZ_LESS_DATETIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/;

/**
 * batch-results 的 createdAt 是不帶時區標記的 UTC 牆鐘字串
 * (例 "2026-08-01T21:55:00");直接餵 new Date() 會被當本地時間,
 * 批次列就顯示 UTC、與完成列(classrecord 帶 Z 的 ISO)差 8 小時(QA 實測)。
 * 這裡補 Z 讓它明確是 UTC,之後沿用完成列同一支 timeProcess() 轉本地顯示。
 * 已帶時區或非日期字串原樣返回 — 不能把已明確的時間再改一次。
 */
export const normalizeBatchUtcTime = (value: string): string => {
  const match = TZ_LESS_DATETIME.exec(value);
  return match ? `${match[1]}T${match[2]}Z` : value;
};

/**
 * 把 batch-results 依 compositionTitle 分進章節表格。
 * - finish 一律略過:已寫回 classrecord,再列就是同一筆顯示兩次(A5)
 * - batchResultID 已在 existingRecordIds(正式紀錄 _id 集合)的也略過:
 *   後端「先寫回 classrecord → 才標 finish」,窗口期這筆同時存在兩邊、
 *   狀態卻還是 running,只靠 finish 判不掉(跨來源去重,kanban 106957)。
 *   classrecord 的 _id 就是 batchResultID(同一筆批次批改,
 *   見 ClassFeedback handleDeleteClassRecord)。缺 ID 的筆不比對,不得誤刪
 * - 對不上章節的 title 收進 unmatchedTitles(去重),不顯示也不炸(B4)
 * - 章節清單為空(尚未載入)時全部不顯示、也不算 unmatched — 判不了是不是改名
 * - 章節內排序與「所有記錄」同規則:座號小到大、同座號時間舊到新
 * - 同座號多筆並存不去重(B5)
 * - createdAt 先過 normalizeBatchUtcTime 補 Z — 顯示端沿用完成列的 timeProcess()
 *   即得本地時間,不另發明格式
 */
export const groupPendingRowsByChapter = (
  items: unknown,
  chapterNames: string[],
  existingRecordIds?: ReadonlySet<string>
): GroupPendingResult => {
  const byChapter: Record<string, PendingBatchRow[]> = {};
  if (!Array.isArray(items) || chapterNames.length === 0) {
    return { byChapter, unmatchedTitles: [] };
  }

  const chapterSet = new Set(chapterNames);
  const unmatched = new Set<string>();

  (items as BatchResultLike[]).forEach((item, index) => {
    const status = classifyBatchStatus(item?.status);
    if (status === "done") return;

    // 跨來源去重:正式紀錄已有同 _id(=batchResultID)就不再列(見函式註解)。
    // 先於章節對映 — 有正式紀錄的筆不是「對不上章節」,不得進 unmatched
    const resultID = item?.batchResultID ?? "";
    if (resultID && existingRecordIds?.has(resultID)) return;

    const title = item?.compositionTitle ?? "";
    if (!chapterSet.has(title)) {
      unmatched.add(title);
      return;
    }

    const seatNo = item?.seatNo ?? "";
    const createdAt = normalizeBatchUtcTime(item?.createdAt ?? "");
    const row: PendingBatchRow = {
      key: resultID || `${title}_${seatNo}_${createdAt}_${index}`,
      resultID,
      seatNo,
      seatNoInt: Number(seatNo) || 0,
      userName: item?.userName ?? "",
      status,
      createdAt,
    };
    if (!byChapter[title]) byChapter[title] = [];
    byChapter[title].push(row);
  });

  Object.values(byChapter).forEach((rows) =>
    rows.sort(
      (a, b) =>
        a.seatNoInt - b.seatNoInt || timeOf(a.createdAt) - timeOf(b.createdAt)
    )
  );

  return { byChapter, unmatchedTitles: [...unmatched] };
};

/**
 * 按「重新批改」成功後的本地狀態轉換:該筆立即改回 open(=grading),
 * 讓該列馬上顯示批改中、hasGradingItems 轉 true 啟動輪詢 — 不等下一輪。
 * 純函式且 immutable(React state 靠新參考觸發 re-render)。
 * 空 resultID 直接原樣返回,不得誤中缺 batchResultID 的筆。
 */
export const markBatchResultReopened = (
  items: BatchResultLike[],
  resultID: string
): BatchResultLike[] => {
  if (!resultID) return items;
  return items.map((item) =>
    item?.batchResultID === resultID ? { ...item, status: "open" } : item
  );
};

/**
 * 按「移除」成功後的本地狀態轉換:把該筆從清單拿掉(對應後端 DELETE
 * /batch-results/{id})。純函式且 immutable;空 resultID 直接原樣返回,
 * 不得誤刪缺 batchResultID 的筆。
 */
export const removeBatchResult = (
  items: BatchResultLike[],
  resultID: string
): BatchResultLike[] => {
  if (!resultID) return items;
  return items.filter((item) => item?.batchResultID !== resultID);
};
